import React from 'react'
import {
  IconButton as MuiIconButton,
  type IconButtonProps as MuiIconButtonProps,
  styled,
} from '@mui/material'
import { type PaletteColor } from '@mui/material/styles'

import { borderRadius, borders, spacing } from '~tokens/internal'
import { getLinkProps } from '~utils/navigationUtils'

type IconButtonColor = 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'default'
type IconButtonVariant = 'text' | 'outlined' | 'contained'

interface IconButtonProps extends Omit<MuiIconButtonProps, 'color'> {
  size?: 'small' | 'medium' | 'large'
  color?: IconButtonColor
  variant?: IconButtonVariant
  to?: string
  newTab?: boolean
}

const StyledIconButton = styled(MuiIconButton)<IconButtonProps>(({
  theme,
  size = 'medium',
  color = 'default',
  variant = 'text',
}) => {
  const getColor = (): string => {
    if (color === 'default') {
      return theme.palette.text.secondary
    }
    return (
      (theme.palette[color as keyof typeof theme.palette] as PaletteColor)?.main ||
      theme.palette.text.secondary
    )
  }

  const iconColor = getColor()

  const buttonSize = ((): { width: string; height: string; padding: string } => {
    switch (size) {
      case 'small':
        return { width: spacing.sm, height: spacing.sm, padding: spacing.micro }
      case 'large':
        return { width: spacing.lg, height: spacing.lg, padding: '1.25rem' }
      default:
        return { width: spacing.md, height: spacing.md, padding: spacing.xxs }
    }
  })()

  const baseStyles = {
    padding: buttonSize.padding,
    width: buttonSize.width,
    height: buttonSize.height,
    flexShrink: 0,
    borderRadius: borderRadius.sm,
    color: iconColor,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
    '&:active': {
      backgroundColor: theme.palette.action.hover,
    },
    '&.Mui-disabled': {
      color: theme.palette.action.disabled,
      '& .material-symbols-rounded, & .material-symbols-outlined': {
        color: `${theme.palette.action.disabled} !important`,
      },
    },
  }

  if (variant === 'contained') {
    const getPaletteColor = (): PaletteColor | undefined => {
      if (color === 'default') return undefined
      return theme.palette[color as keyof typeof theme.palette] as PaletteColor | undefined
    }
    const palette = getPaletteColor()
    return {
      ...baseStyles,
      color: palette ? palette.contrastText : theme.palette.common.white,
      backgroundColor: palette ? palette.main : theme.palette.primary.main,
      '&:hover': {
        backgroundColor: palette ? palette.dark : theme.palette.primary.dark,
      },
      '&:active': {
        backgroundColor: palette ? palette.dark : theme.palette.primary.dark,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
        '& .material-symbols-rounded, & .material-symbols-outlined': {
          color: `${theme.palette.action.disabled} !important`,
        },
      },
    }
  }

  if (variant === 'outlined') {
    return {
      ...baseStyles,
      border: `${borders.width.thin} solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.background.default,
      '&:hover': {
        backgroundColor: theme.palette.background.default,
        border: `${borders.width.thin} solid ${theme.palette.divider}`,
        boxShadow: `inset 0 0 0 100px ${theme.palette.action.hover}`,
      },
      '&:active': {
        backgroundColor: theme.palette.background.default,
        border: `${borders.width.thin} solid ${theme.palette.divider}`,
        boxShadow: `inset 0 0 0 100px ${theme.palette.action.hover}`,
      },
    }
  }

  return baseStyles
})

const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ children, className, to, newTab = false, ...props }, ref) => {
    const linkProps = getLinkProps(to, newTab)

    return (
      <StyledIconButton ref={ref} className={className} {...props} {...linkProps}>
        {children}
      </StyledIconButton>
    )
  },
)

IconButton.displayName = 'IconButton'

export default IconButton
